import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { fetchGTLDetail, updateGTL, fetchPersone, addGTLMember, updateGTLMember, removeGTLMember, exportGTLMembers, fetchOrdini } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Edit, UserPlus, Trash2, Download, Users, Calendar, FileText, Building2 } from 'lucide-react';
import { toast } from 'sonner';

// Format date from YYYY-MM-DD to DD/MM/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const [year, month, day] = dateStr.split('-');
    if (year && month && day) {
      return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
};

export default function GTLDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSegretario, isAdminOrdine, isCoordinatoreGTL, user } = useAuth();
  
  const [gtl, setGtl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [persone, setPersone] = useState([]);
  const [ordini, setOrdini] = useState([]);
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editedGTL, setEditedGTL] = useState(null);
  
  const [addMemberModal, setAddMemberModal] = useState(false);
  const [newMember, setNewMember] = useState({ persona_id: '', ruolo_nel_gtl: 'membro', contatti: '', note: '' });
  
  const [editMemberModal, setEditMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  
  const [deleteMemberConfirm, setDeleteMemberConfirm] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  
  const [saving, setSaving] = useState(false);
  
  // Read-only for Coordinatore GTL
  const isReadOnly = isCoordinatoreGTL();

  useEffect(() => {
    loadGTL();
    loadPersone();
    loadOrdini();
  }, [id]);

  const loadOrdini = async () => {
    try {
      const data = await fetchOrdini({ limit: 200 });
      setOrdini(data.items || data);
    } catch (error) {
      console.error('Error loading ordini:', error);
    }
  };

  // Helper to get ordine name by ID
  const getOrdineName = (ordineId) => {
    if (!ordineId) return null;
    const ordine = ordini.find(o => o.id === ordineId);
    return ordine ? ordine.denominazione : null;
  };

  const loadGTL = async () => {
    setLoading(true);
    try {
      const data = await fetchGTLDetail(id);
      setGtl(data);
      setEditedGTL(data);
    } catch (error) {
      console.error('Error loading GTL:', error);
      toast.error('Errore nel caricamento del GTL');
    } finally {
      setLoading(false);
    }
  };

  const loadPersone = async () => {
    try {
      const data = await fetchPersone({ limit: 200 });
      setPersone(data.items);
    } catch (error) {
      console.error('Error loading persone:', error);
    }
  };

  const handleSaveGTL = async () => {
    setSaving(true);
    try {
      await updateGTL(id, {
        nome: editedGTL.nome,
        tema: editedGTL.tema,
        descrizione: editedGTL.descrizione,
        data_inizio: editedGTL.data_inizio,
        data_fine: editedGTL.data_fine,
        stato: editedGTL.stato
      });
      toast.success('GTL aggiornato con successo');
      setEditModalOpen(false);
      loadGTL();
    } catch (error) {
      console.error('Error saving GTL:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async () => {
    if (!newMember.persona_id) {
      toast.error('Seleziona una persona');
      return;
    }
    setSaving(true);
    try {
      const response = await addGTLMember(id, newMember);
      
      // Check if it's a proposal or direct addition
      if (response.message && response.message.includes('proposta')) {
        toast.success('Proposta inviata! In attesa di approvazione dal Superadmin');
      } else {
        toast.success('Membro aggiunto con successo');
      }
      
      setAddMemberModal(false);
      setNewMember({ persona_id: '', ruolo_nel_gtl: 'membro', contatti: '', note: '' });
      loadGTL();
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiunta');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMember = async () => {
    if (!selectedMember) return;
    setSaving(true);
    try {
      await updateGTLMember(selectedMember.id, {
        ruolo_nel_gtl: selectedMember.ruolo_nel_gtl,
        contatti: selectedMember.contatti,
        note: selectedMember.note
      });
      toast.success('Membro aggiornato con successo');
      setEditMemberModal(false);
      loadGTL();
    } catch (error) {
      console.error('Error updating member:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToDelete) return;
    try {
      await removeGTLMember(memberToDelete.id);
      toast.success('Membro rimosso con successo');
      setDeleteMemberConfirm(false);
      setMemberToDelete(null);
      loadGTL();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la rimozione');
    }
  };

  const handleExport = () => {
    exportGTLMembers(id);
    toast.success('Download avviato');
  };

  const getStatoBadge = (stato) => {
    switch (stato) {
      case 'attivo':
        return <span className="badge-success">Attivo</span>;
      case 'concluso':
        return <span className="badge-neutral">Concluso</span>;
      case 'sospeso':
        return <span className="badge-warning">Sospeso</span>;
      default:
        return <span className="badge-neutral">{stato}</span>;
    }
  };

  const getRuoloBadge = (ruolo) => {
    switch (ruolo) {
      case 'coordinatore':
        return <span className="badge-success">Coordinatore</span>;
      case 'referente':
        return <span className="bg-blue-100 text-blue-700 border border-blue-200 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold">Referente</span>;
      case 'membro_speciale':
        return <span className="bg-purple-100 text-purple-700 border border-purple-200 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold">Membro Speciale</span>;
      default:
        return <span className="badge-neutral">Membro</span>;
    }
  };

  // Get available persone (not already members)
  const availablePersone = persone.filter(p => 
    !gtl?.membri?.some(m => m.persona_id === p.id)
  );

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!gtl) {
    return (
      <div className="p-6 lg:p-8">
        <p className="text-center text-slate-500">GTL non trovato</p>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="gtl-detail-page">
      {/* Header */}
      <div className="page-header -mx-6 lg:-mx-8 -mt-6 lg:-mt-8 mb-8 px-6 lg:px-8 py-6">
        {/* Hide back button for Coordinatore GTL */}
        {!isCoordinatoreGTL() && (
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/gtl')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Indietro
            </Button>
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl font-semibold text-slate-900">{gtl.nome}</h1>
              {getStatoBadge(gtl.stato)}
            </div>
            <p className="text-slate-500 mt-1">{gtl.tema}</p>
          </div>
          <div className="flex gap-2">
            {isSegretario() && (
              <>
                <Button variant="outline" onClick={handleExport} data-testid="export-gtl-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Esporta CSV
                </Button>
                <Button onClick={() => setEditModalOpen(true)} data-testid="edit-gtl-btn">
                  <Edit className="w-4 h-4 mr-2" />
                  Modifica
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="card-institutional">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-slate-600 mb-2">
              <FileText className="w-5 h-5" />
              <span className="text-sm font-medium">Descrizione</span>
            </div>
            <p className="text-sm text-slate-700">{gtl.descrizione || 'Nessuna descrizione'}</p>
          </CardContent>
        </Card>
        
        <Card className="card-institutional">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-slate-600 mb-2">
              <Calendar className="w-5 h-5" />
              <span className="text-sm font-medium">Periodo</span>
            </div>
            <div className="text-sm text-slate-700">
              {gtl.data_inizio && <p>Inizio: {formatDate(gtl.data_inizio)}</p>}
              {gtl.data_fine && <p>Fine: {formatDate(gtl.data_fine)}</p>}
              {!gtl.data_inizio && !gtl.data_fine && <p>Date non definite</p>}
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-institutional">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-slate-600 mb-2">
              <Users className="w-5 h-5" />
              <span className="text-sm font-medium">Membri</span>
            </div>
            <p className="stat-value text-3xl">{gtl.membri?.length || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Members */}
      <Card className="card-institutional">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg">Membri del GTL</CardTitle>
        </CardHeader>
        <CardContent>
          {gtl.membri?.length === 0 ? (
            <div className="empty-state py-12">
              <Users className="empty-state-icon" />
              <p className="empty-state-title">Nessun membro</p>
              <p className="empty-state-description">
                Questo GTL non ha ancora membri
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="membri-table">
                <thead>
                  <tr>
                    <th>Nome e Cognome</th>
                    <th>Ruolo</th>
                    <th>Ordine di Appartenenza</th>
                    <th>Email</th>
                    <th>Cellulare</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const sortedMembers = [...(gtl.membri || [])].sort((a, b) => {
                      const getPriority = (ruolo) => {
                        if (ruolo === 'coordinatore') return 0;
                        if (ruolo === 'referente') return 1;
                        if (ruolo === 'membro') return 2;
                        if (ruolo === 'membro_speciale') return 3;
                        return 4;
                      };
                      
                      const priorityA = getPriority(a.ruolo_nel_gtl);
                      const priorityB = getPriority(b.ruolo_nel_gtl);
                      
                      if (priorityA !== priorityB) return priorityA - priorityB;
                      
                      const cognomeA = a.persona?.cognome || '';
                      const cognomeB = b.persona?.cognome || '';
                      return cognomeA.localeCompare(cognomeB, 'it', { sensitivity: 'base' });
                    });

                    return sortedMembers.map((member) => {
                      const ordineName = getOrdineName(member.persona?.ordine_appartenenza_id || member.persona?.ordine_id);
                      const isMembroSpeciale = member.ruolo_nel_gtl === 'membro_speciale';
                      
                      return (
                        <tr key={member.id} data-testid={`member-row-${member.id}`}>
                          <td>
                            <span className="font-medium">
                              {member.persona?.nome} {member.persona?.cognome}
                            </span>
                          </td>
                          <td>{getRuoloBadge(member.ruolo_nel_gtl)}</td>
                          <td>
                            {isMembroSpeciale ? (
                              <span className="text-slate-400">-</span>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-slate-400" />
                                <span>{ordineName || '-'}</span>
                              </div>
                            )}
                          </td>
                          <td>
                            {member.persona?.email ? (
                              <a href={`mailto:${member.persona.email}`} className="text-blue-600 hover:underline">
                                {member.persona.email}
                              </a>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td>
                            {member.contatti || member.persona?.cellulare ? (
                              <a href={`tel:${member.contatti || member.persona.cellulare}`} className="hover:text-slate-900">
                                {member.contatti || member.persona.cellulare}
                              </a>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit GTL Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-lg" data-testid="edit-gtl-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifica GTL</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome GTL *</Label>
              <Input
                value={editedGTL?.nome || ''}
                onChange={(e) => setEditedGTL({ ...editedGTL, nome: e.target.value })}
                data-testid="edit-gtl-nome"
              />
            </div>
            <div className="space-y-2">
              <Label>Tema *</Label>
              <Input
                value={editedGTL?.tema || ''}
                onChange={(e) => setEditedGTL({ ...editedGTL, tema: e.target.value })}
                data-testid="edit-gtl-tema"
              />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea
                value={editedGTL?.descrizione || ''}
                onChange={(e) => setEditedGTL({ ...editedGTL, descrizione: e.target.value })}
                rows={3}
                data-testid="edit-gtl-descrizione"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Inizio</Label>
                <Input
                  type="date"
                  value={editedGTL?.data_inizio || ''}
                  onChange={(e) => setEditedGTL({ ...editedGTL, data_inizio: e.target.value })}
                  data-testid="edit-gtl-data-inizio"
                />
              </div>
              <div className="space-y-2">
                <Label>Data Fine</Label>
                <Input
                  type="date"
                  value={editedGTL?.data_fine || ''}
                  onChange={(e) => setEditedGTL({ ...editedGTL, data_fine: e.target.value })}
                  data-testid="edit-gtl-data-fine"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Stato</Label>
              <Select value={editedGTL?.stato || 'attivo'} onValueChange={(v) => setEditedGTL({ ...editedGTL, stato: v })}>
                <SelectTrigger data-testid="edit-gtl-stato">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="sospeso">Sospeso</SelectItem>
                  <SelectItem value="concluso">Concluso</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>Annulla</Button>
            <Button onClick={handleSaveGTL} disabled={saving} data-testid="save-edit-gtl-btn">
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Modal */}
      <Dialog open={addMemberModal} onOpenChange={setAddMemberModal}>
        <DialogContent data-testid="add-member-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Aggiungi Membro</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Persona *</Label>
              <Select value={newMember.persona_id} onValueChange={(v) => setNewMember({ ...newMember, persona_id: v })}>
                <SelectTrigger data-testid="select-persona">
                  <SelectValue placeholder="Seleziona persona" />
                </SelectTrigger>
                <SelectContent>
                  {availablePersone.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.titolo} {p.nome} {p.cognome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ruolo nel GTL</Label>
              <Select value={newMember.ruolo_nel_gtl} onValueChange={(v) => setNewMember({ ...newMember, ruolo_nel_gtl: v })}>
                <SelectTrigger data-testid="select-ruolo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="membro">Membro</SelectItem>
                  <SelectItem value="coordinatore">Coordinatore</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Contatti specifici</Label>
              <Input
                value={newMember.contatti}
                onChange={(e) => setNewMember({ ...newMember, contatti: e.target.value })}
                placeholder="Email o telefono specifico per il GTL"
                data-testid="member-contatti"
              />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={newMember.note}
                onChange={(e) => setNewMember({ ...newMember, note: e.target.value })}
                placeholder="Note aggiuntive"
                rows={2}
                data-testid="member-note"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMemberModal(false)}>Annulla</Button>
            <Button onClick={handleAddMember} disabled={saving} data-testid="confirm-add-member">
              {saving ? 'Aggiunta...' : 'Aggiungi'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Modal */}
      <Dialog open={editMemberModal} onOpenChange={setEditMemberModal}>
        <DialogContent data-testid="edit-member-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifica Membro</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="font-medium text-slate-900">
                {selectedMember?.persona?.titolo} {selectedMember?.persona?.nome} {selectedMember?.persona?.cognome}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Ruolo nel GTL</Label>
              <Select 
                value={selectedMember?.ruolo_nel_gtl || 'membro'} 
                onValueChange={(v) => setSelectedMember({ ...selectedMember, ruolo_nel_gtl: v })}
              >
                <SelectTrigger data-testid="edit-member-ruolo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="membro">Membro</SelectItem>
                  <SelectItem value="coordinatore">Coordinatore</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Contatti specifici</Label>
              <Input
                value={selectedMember?.contatti || ''}
                onChange={(e) => setSelectedMember({ ...selectedMember, contatti: e.target.value })}
                data-testid="edit-member-contatti"
              />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={selectedMember?.note || ''}
                onChange={(e) => setSelectedMember({ ...selectedMember, note: e.target.value })}
                rows={2}
                data-testid="edit-member-note"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMemberModal(false)}>Annulla</Button>
            <Button onClick={handleUpdateMember} disabled={saving} data-testid="save-edit-member">
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Member Confirmation */}
      <AlertDialog open={deleteMemberConfirm} onOpenChange={setDeleteMemberConfirm}>
        <AlertDialogContent data-testid="delete-member-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma rimozione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler rimuovere {memberToDelete?.persona?.nome} {memberToDelete?.persona?.cognome} dal GTL?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleRemoveMember}
              data-testid="confirm-remove-member"
            >
              Rimuovi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
